import { ArrowUpDown, Check, ChevronDown } from "@/components/ui/icons";
import { Button } from "react-aria-components/Button";
import { Header } from "react-aria-components/Header";
import { Menu, MenuItem, MenuSection, MenuTrigger } from "react-aria-components/Menu";
import { Popover } from "react-aria-components/Popover";

import { cn } from "@/lib/ui/cn";
import { useThemeColors } from "@/theme/theme";
import type { MenuPillProps } from "./MenuPill.types";
import "@/components/desktop/desktop.web.css";

export type { MenuPillOption } from "./MenuPill.types";

/**
 * `MenuPill` on the web: the same pill ("Sort: Newest", "Location: Slabs"), opening a React Aria menu
 * anchored to it instead of the phone's bottom sheet, as the workspace switcher does. The menu is
 * a radio group, so a keyboard opens it with Enter or Space, moves with the arrow keys and picks
 * with Enter, and a screen reader hears which choice is current. It is drawn with the shell's
 * `desktop-menu` styles, so every web menu looks like one family.
 */
export function MenuPill<K extends string>(
  {
    label,
    a11yLabel,
    title,
    options,
    value,
    onPick,
    testID,
    indicator,
    activeGlyph,
    prefix = "Sort",
    icon,
    tinted = false,
    className,
  }: MenuPillProps<K>,
) {
  const c = useThemeColors();
  return (
    <MenuTrigger>
      <Button
        data-testid={testID}
        aria-label={a11yLabel}
        className={cn(
          "flex min-h-9 cursor-pointer flex-row items-center gap-1.5 rounded-full border px-3 py-1.5",
          tinted ? "border-primary bg-accent-soft" : "border-border bg-surface",
          className,
        )}
        // The pill sits flush in a horizontal scroller, which clips anything outside it, so the
        // focus ring is drawn just inside the edge. Inline to beat global.css's unlayered offset.
        style={{ outlineOffset: -2 }}
      >
        {icon ?? <ArrowUpDown size={14} color={c.mutedForeground} aria-hidden />}
        <span className="whitespace-nowrap font-label text-xs text-foreground">
          {prefix === null ? label : `${prefix}: ${label}`}
        </span>
        {indicator ?? <ChevronDown size={14} color={c.mutedForeground} aria-hidden />}
      </Button>
      <Popover className="desktop-menu" placement="bottom start" offset={6}>
        <Menu
          className="outline-none"
          selectionMode="single"
          disallowEmptySelection
          selectedKeys={[value]}
          // `onAction` fires on every pick, so picking the current choice again still reports it,
          // as the phone sheet does, and a sort with a direction can reverse.
          onAction={(key) => {
            const picked = options.find((option) => option.value === key);
            if (picked) onPick(picked.value);
          }}
        >
          <MenuSection>
            <Header className="desktop-menu-label">{title}</Header>
            {options.map((option) => (
              <MenuItem
                key={option.value}
                id={option.value}
                textValue={option.label}
                className="desktop-menu-item"
                aria-label={option.a11yLabel}
                data-testid={option.testID}
              >
                {({ isSelected }) => (
                  <>
                    <span className="flex flex-col">
                      <span className="font-body text-sm">{option.label}</span>
                      {option.hint
                        ? (
                          <span className="font-body text-xs text-muted-foreground">
                            {option.hint}
                          </span>
                        )
                        : null}
                    </span>
                    {isSelected
                      ? activeGlyph ?? <Check size={15} color={c.primary} aria-hidden />
                      : null}
                  </>
                )}
              </MenuItem>
            ))}
          </MenuSection>
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
